import { useEffect, useState } from "react";
import {
  FEHLER_SICHTBAR_MAX,
  alleFehlerSchliessen,
  fehlerSchliessen,
  useFehlerStapel,
} from "../../state/fehlerStapel.ts";

/**
 * Die Anzeige des Fehlerstapels (Fehleranzeige Paket 1, Spec
 * `docs/superpowers/specs/2026-10-19-fehleranzeige-fundament-design.md`, Abschnitt 1). Der Speicher
 * dahinter steht in `state/fehlerStapel.ts`; gemeldet wird mit `meldeFehler`, nicht ueber Props.
 *
 * BEWUSSTE ABWEICHUNG VOM BESTAND: `index.html` kennt fuer Fehler nur den Toast. Hier bleibt jede
 * Meldung stehen, bis man sie schliesst (siehe Kopfkommentar von `Toast.tsx`).
 *
 * Eingehaengt wird die Komponente in `src/App.tsx` neben `<Speicher>`, nicht in
 * `components/App/App.tsx`: nur dort erscheint sie auf JEDER Flaeche, auch auf der Anmeldemaske.
 *
 * - Ist der Stapel leer, kommt `null` zurueck - kein Huellknoten. Die Visualtests vergleichen den
 *   ganzen `<body>`.
 * - Jeder Eintrag ist ein eigenes `role="alert"`; der Schliessen-Knopf steht NEBEN dem Text, nicht
 *   darin, damit Screenreader nicht bei jeder Meldung "Meldung schliessen" mitlesen. Der Zaehler
 *   steht dagegen IM Alarm: steigt er, wird die Meldung erneut angesagt.
 * - Der Text wird nie gekuerzt. Lange Saetze (etwa die Warnung "steht bereits in JTL ... bitte
 *   NICHT erneut speichern") brechen um, das Stylesheet erlaubt das ausdruecklich.
 * - Sichtbar sind hoechstens `FEHLER_SICHTBAR_MAX` Eintraege. "+ N weitere" blendet die aelteren ein;
 *   ein Zuklappen gibt es nicht, der Nutzer schliesst sie stattdessen. Schrumpft der Stapel wieder
 *   auf das Maximum oder darunter, klappt er von selbst zu - sonst stuende er beim naechsten
 *   Anwachsen offen, obwohl der Nutzer genau diese Eintraege nie aufgeklappt hat.
 * - "Alle schliessen" gibt es ab zwei Eintraegen. Gezaehlt werden Eintraege, nicht Wiederholungen.
 */
export function FehlerStapel() {
  const eintraege = useFehlerStapel();
  const [aufgeklappt, setAufgeklappt] = useState(false);

  const ueberschuss = eintraege.length - FEHLER_SICHTBAR_MAX;
  useEffect(() => {
    if (ueberschuss <= 0) setAufgeklappt(false);
  }, [ueberschuss]);

  if (eintraege.length === 0) return null;

  const sichtbar = aufgeklappt ? eintraege : eintraege.slice(0, FEHLER_SICHTBAR_MAX);
  const zusammengefasst = ueberschuss > 0 && !aufgeklappt;

  return (
    <div className="fehler-stapel">
      {sichtbar.map((eintrag) => (
        <div key={eintrag.id} className="fehler-stapel-eintrag">
          <div role="alert" id={`fehler-stapel-text-${eintrag.id}`} className="fehler-stapel-text">
            {eintrag.text}
            {eintrag.anzahl > 1 && <span className="fehler-stapel-zaehler">{eintrag.anzahl}×</span>}
          </div>
          <button
            type="button"
            className="fehler-stapel-schliessen"
            aria-label="Meldung schließen"
            aria-describedby={`fehler-stapel-text-${eintrag.id}`}
            onClick={() => fehlerSchliessen(eintrag.id)}
          >
            ×
          </button>
        </div>
      ))}
      {(zusammengefasst || eintraege.length > 1) && (
        <div className="fehler-stapel-aktionen">
          {zusammengefasst && (
            <button type="button" className="fehler-stapel-knopf" onClick={() => setAufgeklappt(true)}>
              {`+ ${ueberschuss} weitere`}
            </button>
          )}
          {eintraege.length > 1 && (
            <button type="button" className="fehler-stapel-knopf" onClick={alleFehlerSchliessen}>
              Alle schließen
            </button>
          )}
        </div>
      )}
    </div>
  );
}
